<template>
  <div id="body">
    <form id="payment-form" @submit.prevent="pay">
      <div id="payment-element">
        <!--Stripe.js injects the Payment Element-->
      </div>
      <button id="submit" type="submit" :disabled="paying || loading">
        <div class="spinner" v-if="paying || loading"></div>
        <span v-else>{{
          `($ ${price} USD) ${$func.parseIso("payNow")}`
        }}</span>
      </button>
      <div id="payment-message" v-if="errorMessage">{{ errorMessage }}</div>
    </form>
  </div>
</template>

<script>
const stripe = Stripe(process.env.VUE_APP_KEY_STRIPE);

export default {
  props: {
    clientSecret: { type: String, required: true },
    price: { type: [Number, String], default: null },
    numberRef: { type: String, required: true },
    paying: { type: Boolean, default: false },
  },
  data() {
    return {
      elements: null,
      paymentElement: null,
      loading: false,
      errorMessage: null,
    };
  },
  mounted() {
    const appearance = { theme: "stripe" };
    const elements = stripe.elements({
      clientSecret: this.clientSecret,
      appearance,
    });
    this.elements = elements;
    const paymentElement = elements.create("payment", {
      wallets: { link: "never" },
    });
    this.paymentElement = paymentElement;
    paymentElement.mount("#payment-element");
  },
  beforeDestroy() {
    if (this.paymentElement) {
      try {
        this.paymentElement.destroy();
      } catch (e) {
        // noop
      }
    }
  },
  methods: {
    pay() {
      this.$emit("pay-clicked");
    },
    async confirmPaymentWithStripe() {
      try {
        this.loading = true;
        this.errorMessage = null;
        const { error } = await stripe.confirmPayment({
          elements: this.elements,
          redirect: "if_required",
          confirmParams: {
            return_url: window.location.href,
          },
        });
        this.loading = false;
        if (error) {
          this.errorMessage =
            error?.message || "An unexpected error occurred.";
        } else {
          await this.confirm();
        }
      } catch (error) {
        console.log(error);
        this.loading = false;
        this.errorMessage = "An unexpected error occurred.";
      }
    },
    async confirm() {
      try {
        await this.$axios.get(
          `/payment/confirm/payment-intent?numberRef=${this.numberRef}`
        );
      } catch (error) {
        const status = error?.response?.status;
        const dataError = error?.response?.data?.error;
        if (status === 409 && dataError === "overpayment_detected") {
          // La API rechazo el cobro para evitar sobre-cobro: mostrar el error,
          // NO emitir success porque la reserva no quedo marcada como paga.
          this.$emit("overpayment");
          if (this.$func && this.$func.parseIso) {
            this.errorMessage =
              this.$func.parseIso("overpaymentDetected") ||
              error?.response?.data?.message ||
              "An unexpected error occurred.";
          } else {
            this.errorMessage =
              error?.response?.data?.message || "An unexpected error occurred.";
          }
          return;
        }
        console.warn("Backend confirm failed, but Stripe payment succeeded", error);
      }
      this.$emit("success");
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

#body {
  font-family: 'Ubuntu', sans-serif;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
}

#payment-form {
  width: 100%;
  align-self: center;
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #F3F4F6;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  padding: 20px;
}

.hidden {
  display: none;
}

#payment-message {
  color: rgb(105, 115, 134);
  font-size: 16px;
  line-height: 20px;
  padding-top: 12px;
  text-align: center;
}

#payment-element {
  margin-bottom: 24px;
}

#submit {
  background: #22C55E;
  font-family: 'Ubuntu', sans-serif;
  color: #ffffff;
  border-radius: 8px;
  border: 0;
  padding: 12px 16px;
  font-size: 19px;
  font-weight: 600;
  cursor: pointer;
  display: block;
  transition: background 0.2s ease;
  box-shadow: 0px 4px 5.5px 0px rgba(0, 0, 0, 0.07);
  max-width: 340px;
  margin: 0 auto;
}
#submit:hover {
  background: #16A34A;
}
#submit:disabled {
  opacity: 0.5;
  cursor: default;
}

.spinner,
.spinner:before,
.spinner:after {
  border-radius: 50%;
}
.spinner {
  color: #ffffff;
  font-size: 22px;
  text-indent: -99999px;
  margin: 0px auto;
  position: relative;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
}
.spinner:before,
.spinner:after {
  position: absolute;
  content: "";
}
.spinner:before {
  width: 10.4px;
  height: 20.4px;
  background: #22C55E;
  border-radius: 20.4px 0 0 20.4px;
  top: -0.2px;
  left: -0.2px;
  -webkit-transform-origin: 10.4px 10.2px;
  transform-origin: 10.4px 10.2px;
  -webkit-animation: loading 2s infinite ease 1.5s;
  animation: loading 2s infinite ease 1.5s;
}
.spinner:after {
  width: 10.4px;
  height: 10.2px;
  background: #22C55E;
  border-radius: 0 10.2px 10.2px 0;
  top: -0.1px;
  left: 10.2px;
  -webkit-transform-origin: 0px 10.2px;
  transform-origin: 0px 10.2px;
  -webkit-animation: loading 2s infinite ease;
  animation: loading 2s infinite ease;
}

@-webkit-keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes loading {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>